import { useMemo } from 'react'
import type { CalendarBoardFilterState, ChecklistItem as FlatChecklistItem, TimelineBoardKey } from '../../types/schema'
import { TIMELINE_BOARD_KEYS } from '../../types/schema'
import type { TimelineElement } from '../../context/TimelineContext'
import type { MeetingRecord } from '../../types/meetings'
import type { HuddleSession } from '../../services/huddleSessionService'
import {
  buildEventsFromHuddleSessions,
  buildEventsFromMeetings,
  buildEventsFromElements,
  buildEventsFromRecurringInstances,
  compareIsoDate,
  resolveVisibility,
  type CalendarEvent,
} from './calendarHelpers'

interface UseCalendarEventsArgs {
  /** Omit for calendar surfaces that intentionally show only meetings/huddles. */
  elements?: TimelineElement[]
  meetings?: MeetingRecord[]
  huddleSessions?: HuddleSession[]
  activeBoard: TimelineBoardKey
  boardScope: { mode: 'current' | 'custom'; boardKeys?: TimelineBoardKey[] }
  filterState: CalendarBoardFilterState
  /** Optional date window — when provided, events are clipped to overlap-only. */
  windowStartIso?: string
  windowEndIso?: string
  /** Optional flat checklist_items feed — used to surface recurring-instance
   * events that aren't reachable through element.checklist. */
  flatChecklistItems?: FlatChecklistItem[]
  /** Optional education class session events. */
  eduSessions?: CalendarEvent[]
  /** Optional education events. */
  eduEvents?: CalendarEvent[]
}

export interface AggregatedEvents {
  /** Events with no time-of-day window. Render in the all-day row. */
  allDay: CalendarEvent[]
  /** Events with both startTime + endTime. Render on the hourly grid. */
  timed: CalendarEvent[]
  /** Full filter-aware event list (used by Month view chips). */
  all: CalendarEvent[]
}

const isWithinWindow = (event: CalendarEvent, startIso?: string, endIso?: string): boolean => {
  if (!startIso || !endIso) return true
  return compareIsoDate(event.endDate, startIso) >= 0 && compareIsoDate(event.startDate, endIso) <= 0
}

const buildAncestorMap = (elements: TimelineElement[]): Map<string, string[]> => {
  const byId = new Map(elements.map((element) => [element.id, element]))
  const ancestors = new Map<string, string[]>()
  for (const element of elements) {
    const chain: string[] = []
    let cursor: string | null = element.parentId
    while (cursor) {
      chain.unshift(cursor)
      cursor = byId.get(cursor)?.parentId ?? null
    }
    ancestors.set(element.id, chain)
  }
  return ancestors
}

export const useCalendarEvents = ({
  elements = [],
  meetings = [],
  huddleSessions = [],
  activeBoard,
  boardScope,
  filterState,
  windowStartIso,
  windowEndIso,
  flatChecklistItems = [],
  eduSessions = [],
  eduEvents = [],
}: UseCalendarEventsArgs): AggregatedEvents => {
  return useMemo(() => {
    const acceptedBoards = new Set<string>()
    if (boardScope.mode === 'custom' && boardScope.boardKeys?.length) {
      for (const key of boardScope.boardKeys) acceptedBoards.add(key)
      acceptedBoards.add(activeBoard)
    } else {
      acceptedBoards.add(activeBoard)
    }
    // Defensive: drop any unknown board key.
    for (const key of Array.from(acceptedBoards)) {
      if (!TIMELINE_BOARD_KEYS.includes(key as TimelineBoardKey)) acceptedBoards.delete(key)
    }

    const ancestorMap = buildAncestorMap(elements)
    const raw = [
      ...buildEventsFromElements(elements, acceptedBoards),
      ...buildEventsFromMeetings(meetings),
      ...buildEventsFromHuddleSessions(huddleSessions),
      ...buildEventsFromRecurringInstances(flatChecklistItems, acceptedBoards),
      ...eduSessions,
      ...eduEvents,
    ]

    const visible = raw.filter((event) => {
      if (!isWithinWindow(event, windowStartIso, windowEndIso)) return false
      // Meetings, huddles, and class sessions have no timeline parent, so the board cascade
      // cannot determine their visibility. They remain subject to date windows.
      if (event.kind === 'meeting' || event.kind === 'huddle' || event.kind === 'session' || event.kind === 'edu_event' || event.kind === 'edu_ptm') return true
      // Walk the cascade chain top-down: rootGoal → parent → self.
      const ancestorChain: string[] = []
      if (event.elementId) {
        ancestorChain.push(...(ancestorMap.get(event.elementId) ?? []))
      } else if (event.parentElementId) {
        ancestorChain.push(...(ancestorMap.get(event.parentElementId) ?? []))
        ancestorChain.push(event.parentElementId)
      }
      const selfId = event.elementId ?? event.checklistItemId ?? event.id
      return resolveVisibility(ancestorChain, selfId, filterState)
    })

    const allDay: CalendarEvent[] = []
    const timed: CalendarEvent[] = []
    for (const event of visible) {
      if (event.startTime && event.endTime && event.startDate === event.endDate) {
        timed.push(event)
      } else {
        allDay.push(event)
      }
    }

    const sortByDate = (a: CalendarEvent, b: CalendarEvent) =>
      a.startDate.localeCompare(b.startDate) || a.title.localeCompare(b.title)
    allDay.sort(sortByDate)
    timed.sort(
      (a, b) =>
        a.startDate.localeCompare(b.startDate) ||
        (a.startTime ?? '').localeCompare(b.startTime ?? '') ||
        a.title.localeCompare(b.title),
    )

    return { allDay, timed, all: visible }
  }, [elements, meetings, huddleSessions, activeBoard, boardScope, filterState, windowStartIso, windowEndIso, flatChecklistItems, eduSessions, eduEvents])
}
